import { Component } from '@theme/component';

/**
 * Holds add to cart on the product page until the shopper picks a size.
 *
 * Horizon always has a variant selected, so the size radios keep their checked
 * state; this element only tracks whether the shopper has chosen a size
 * themselves. It lives outside the variant picker so variant re-renders keep
 * its state. Styles key off its data-pending and data-error attributes.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - Announces the error message.
 *
 * @extends {Component<Refs>}
 */
class BrsSizeGuard extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {AbortController | undefined} */
  #listenerController;

  connectedCallback() {
    super.connectedCallback();

    this.#listenerController?.abort();
    this.#listenerController = new AbortController();
    const { signal } = this.#listenerController;

    // Capture on window so the guard runs before the theme's document-level handlers.
    window.addEventListener('click', this.#handleClick, { capture: true, signal });
    window.addEventListener('submit', this.#handleSubmit, { capture: true, signal });
    window.addEventListener('change', this.#handleChange, { capture: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#listenerController?.abort();
    this.#listenerController = undefined;
  }

  get #section() {
    return this.parentElement;
  }

  get #sizeFieldset() {
    return this.#section?.querySelector('[data-brs-size-option]') ?? null;
  }

  get #pending() {
    return this.hasAttribute('data-pending') && this.#sizeFieldset !== null;
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.#section?.contains(target)) return;

    if (target.closest('[data-brs-size-option] input')) {
      this.#choose();
      return;
    }

    if (!this.#pending || !target.closest('.add-to-cart-button')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#showError();
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
    if (!this.#section?.contains(form) || !this.#pending) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#showError();
  };

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.#section?.contains(target)) return;
    if (target.closest('[data-brs-size-option]')) this.#choose();
  };

  #choose() {
    if (!this.hasAttribute('data-pending') && !this.hasAttribute('data-error')) return;

    this.removeAttribute('data-pending');
    this.removeAttribute('data-error');
    this.refs.liveRegion.textContent = '';
  }

  #showError() {
    const fieldset = this.#sizeFieldset;
    if (!fieldset) return;

    this.setAttribute('data-error', '');

    // Clear first so a repeated attempt is announced again.
    const { liveRegion } = this.refs;
    liveRegion.textContent = '';
    requestAnimationFrame(() => {
      liveRegion.textContent = this.dataset.message ?? '';
    });

    const bounds = fieldset.getBoundingClientRect();
    if (bounds.top < 0 || bounds.bottom > window.innerHeight) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      fieldset.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    const firstSize = fieldset.querySelector('input:not([aria-disabled="true"])') ?? fieldset.querySelector('input');
    if (firstSize instanceof HTMLInputElement) firstSize.focus({ preventScroll: true });
  }
}

if (!customElements.get('brs-size-guard')) {
  customElements.define('brs-size-guard', BrsSizeGuard);
}
